/**
 * Flow control for authentication orchestrator
 * Handles opening, closing, and completing the auth flow
 */

import { nextTick } from 'vue';
import type { ComputedRef } from 'vue';
import type { AuthPhase } from '@/types/profileTypes';
import type VueRouter from 'vue-router';
import type { Route } from 'vue-router';
import type { Store } from 'vuex';
import { profileStorage } from '@/services/profileStorage';
import {
  loadEnhancedAuthState,
  validateAuthState,
  clearEnhancedAuthState
} from '@/utils/auth-state-persistence';
import {
  PortfolioUpdateType,
  type UpdatePortfolioMutation,
  type AuthUserOutput,
  type UserPortfolioQuery,
  type PortfolioCompletionStatus
} from '@/generated/nexus-graphql';
import { MutationTypes as UserMutationTypes } from '@/store/modules/user/mutations';
import type { AccessPolicy } from '@/types/accessPolicy';
import type { ProgramJoinContext } from '@/types/programJoinContext';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import type { RootState, UpdatePortfolioInput } from '../types';
import { ERROR_MESSAGES, UI_TIMEOUTS, NON_RECOVERABLE_PHASES } from '../constants';
import type { NavigationService } from './useAuthNavigationService';
import type { AuthOrchestratorState } from './useAuthState';
import { createDebugLogger } from '../utils/debug';
import { authResourceManager } from '../utils/cleanup';

const logger = createDebugLogger('FlowControl');

/**
 * Deep-clone portfolio data into a plain, extensible object before it is stored
 * in the orchestrator's reactive state.
 *
 * Portfolio objects come straight from the Apollo cache, which freezes query
 * results (they become non-extensible). The orchestrator exposes its state as
 * `readonly(state)` (see useAuthState.ts), and Vue 2.7's `readonly()` proxy
 * caches itself on the raw target via `Object.defineProperty(target,
 * '__v_rawToReadonly', …)`. That call throws "Cannot define property
 * __v_rawToReadonly, object is not extensible" the moment a template reads a
 * frozen role object (e.g. getRoleData in UnifiedAuthDialog.vue), crashing the
 * whole auth dialog. Cloning yields an extensible copy so the readonly proxy can
 * be created safely. (Sentry PCV4MAXPRO-MC)
 */
function toExtensibleProfileData<T>(value: T): T {
  return value == null ? value : (JSON.parse(JSON.stringify(value)) as T);
}

// Helper function for safe navigation
function safeNavigate(
  router: VueRouter | undefined,
  target: Record<string, unknown> | string,
  replace = false
) {
  if (!router) {
    logger.warn('Router not available for navigation');
    return;
  }
  const routerInst = router as VueRouter;
  const nav = replace ? routerInst.replace(target) : routerInst.push(target);
  if (nav && nav.catch) {
    nav.catch((err: Error) => {
      if (err && err.name !== 'NavigationDuplicated') {
        logger.error('Navigation error:', err);
      }
    });
  }
}

// Resolve where to send the user once auth completes.
// An explicit redirect (deep-link / ?redirect=) always wins. Otherwise, if the
// user is already sitting on a PUBLIC content page (a sponsor landing or a
// public program link), keep them there instead of bouncing to the portfolio —
// that bounce was cancelling the landing's in-flight queries (infinite hero
// spinner) and surprising authed users who clicked a public link. Only fall
// back to /portfolio/my-programs when there is no public page to remain on.
function resolvePostAuthRedirect(
  originalRedirect: string | null,
  router?: VueRouter
): string | { path: string } {
  if (originalRedirect) return originalRedirect;
  const current = (router as VueRouter | undefined)?.currentRoute;
  const fullPath = current?.fullPath || '';
  const name = current?.name;
  const isPublicContent =
    /^\/sponsor\//.test(fullPath) ||
    /[?&]program=/.test(fullPath) ||
    name === 'publicProgram' ||
    name === 'demoProgram' ||
    name === 'sponsor';
  return isPublicContent ? fullPath : { path: '/portfolio/my-programs' };
}

export function useAuthFlowControl(
  state: AuthOrchestratorState,
  deps: {
    router?: VueRouter;
    route?: Route;
    rootStore?: Store<RootState>;
    getUser?: ComputedRef<AuthUserOutput | null>;
    updatePortfolioMutate?: (params: { input: UpdatePortfolioInput }) => Promise<{
      data?: UpdatePortfolioMutation;
      errors?: Array<{ message: string }>;
    }>;
    clearStoredProfileState: () => void;
    saveProfileStateToStorage: () => void;
    determineIncompletePhase?: (
      userData: AuthUserOutput | null,
      portfolioData: UserPortfolioQuery | null
    ) => AuthPhase | null;
    loadPortfolioQuery?: () => Promise<{
      data?: UserPortfolioQuery;
      errors?: Array<{ message: string }>;
    }>;
    navigationService?: NavigationService;
  }
) {
  /**
   * Close the auth dialog and reset state
   */
  const closeAuth = async () => {
    // Non-dismissible mode: refuse user-triggered close.
    // The legitimate post-auth-complete close path goes through completeAndRedirect
    // which sets state.isOpen = false directly (bypassing this function).
    if (state.nondismissible) {
      logger.log('closeAuth blocked: dialog is in nondismissible mode');
      return;
    }
    logger.log('Closing auth');

    // Cleanup all managed resources
    authResourceManager.cleanup();

    // Clear any stuck non-recoverable phases before closing
    // This ensures we don't get stuck in phases like accountDiscovery
    if (NON_RECOVERABLE_PHASES.includes(state.phase) && state.phase !== 'idle') {
      logger.log(`Clearing non-recoverable phase: ${state.phase}`);
    }

    state.isOpen = false;
    if (deps.navigationService) {
      await deps.navigationService.systemTransition('idle');
    } else {
      state.phase = 'idle'; // Fallback
    }
    state.previousPhase = 'idle';
    state.loading = false;
    state.error = null;
    state.contact = '';
    state.originalRedirect = null;
    state.selectedUserTypes = [];
    state.currentRoleIndex = 0;
    state.profileData = {};
    state.portfolioCompletionStatus = null;
    state.recoveryAvailable = false;

    // Clear stored profile state on close
    deps.clearStoredProfileState();
    profileStorage.clearSession();

    // Remove openLoginPopup route param to prevent modal from re-opening
    if (deps.router && deps.router.currentRoute?.params?.openLoginPopup === 'true') {
      const cleanedParams = { ...deps.router.currentRoute.params } as Record<string, string>;
      delete cleanedParams.openLoginPopup;
      const cleanQuery = { ...deps.router.currentRoute.query } as Record<string, string | string[]>;
      delete cleanQuery.redirect;
      deps.router
        .replace({
          name: deps.router.currentRoute.name || 'landing',
          params: cleanedParams,
          query: cleanQuery
        })
        .catch((err: Error) => {
          // Only log if it's not a navigation duplicate error
          if (err.name !== 'NavigationDuplicated') {
            logger.error('Failed to clean route params:', err);
          }
        });
    }
  };

  /**
   * Complete auth flow and redirect
   */
  const completeAndRedirect = async () => {
    logger.log('Completing flow, redirecting to:', state.originalRedirect);

    try {
      // Ensure loading state is cleared
      state.loading = false;

      // Determine redirect target - always ensure we have a fallback
      const redirectTarget = resolvePostAuthRedirect(state.originalRedirect, deps.router);
      logger.log('Using redirect target:', redirectTarget);

      // Ensure we're in complete phase for UI display
      if (state.phase !== 'complete') {
        if (deps.navigationService) {
          await deps.navigationService.completeFlow(state.originalRedirect);
        } else {
          state.phase = 'complete'; // Fallback
        }
      }

      state.isOpen = true; // Keep dialog open to show success briefly

      // Show success state briefly
      await new Promise(resolve => {
        authResourceManager.setTimeout(() => resolve(undefined), UI_TIMEOUTS.SUCCESS_DISPLAY);
      });

      // Mark profile as complete before navigating
      localStorage.setItem('profileComplete', 'true');
      if (deps.rootStore) {
        deps.rootStore.commit(`user/${UserMutationTypes.SET_PROFILE_COMPLETE}`, true);
        await nextTick();
      }

      // Record the onboarding-complete FACT on the server (durable source of
      // truth for the completeness gate — see auth-linking.service.ts). This is
      // what lets the gate stop re-deriving completeness from portfolio docs.
      // Best-effort and non-blocking: a failure here must never stall the
      // redirect (the local flag above already lets the user through this session).
      const stampOnboardingComplete = async () => {
        try {
          const { default: nexusClient } = await import('@/apollo/nexusClient');
          const { default: gql } = await import('graphql-tag');
          await nexusClient.mutate({
            mutation: gql`
              mutation AuthMarkProfileOnboardingComplete {
                authMarkProfileOnboardingComplete
              }
            `
          });
        } catch (markErr) {
          logger.warn('markProfileOnboardingComplete failed (non-blocking):', markErr);
        }
      };
      // Fire-and-forget; never block the redirect on it.
      stampOnboardingComplete().catch(() => undefined);

      // Clear intermediate auth state to prevent bootloop on reload
      clearEnhancedAuthState();

      // Navigate via SPA router to avoid full page reload loop
      logger.log('SPA navigating to:', redirectTarget);
      const targetPath =
        typeof redirectTarget === 'string'
          ? redirectTarget
          : redirectTarget.path || '/portfolio/my-programs';

      // Lift the nondismissible lock and clear the auto-open guard so a future
      // logout + revisit can re-trigger the unauth flow cleanly.
      state.nondismissible = false;
      try {
        sessionStorage.removeItem('auth_dialog_auto_opened');
      } catch (_) {
        /* sessionStorage may be unavailable (private mode, SSR) — non-fatal */
      }

      state.isOpen = false;

      // Shared program link path (/guide/{programId}/{page}): ParticipantGuide.vue's
      // setup() already ran in unauth mode with synthetic Student data. A SPA router
      // push won't re-run setup, so the participant view stays in anon state forever.
      // Force a full reload to re-trigger setup with the now-authed user — the existing
      // createStudent() auto-enrollment in ParticipantGuide will then fire naturally.
      if (
        typeof targetPath === 'string' &&
        (targetPath.startsWith('/guide/') || targetPath.startsWith('/sign/'))
      ) {
        logger.log('Forcing full reload for post-auth re-render:', targetPath);
        window.location.assign(targetPath);
        return;
      }

      safeNavigate(deps.router, targetPath, true);
    } catch (error) {
      logger.error('Error in completeAndRedirect:', error);
      // Ensure loading state is cleared even on error
      state.loading = false;
      state.error = error instanceof Error ? error.message : 'Failed to complete authentication';
    }
  };

  /**
   * Complete profile setup and redirect
   */
  const completeProfileAndRedirect = async () => {
    logger.log('Completing profile setup');
    state.loading = true;

    try {
      // Save general profile data first using GraphQL
      if (state.profileData.general && deps.updatePortfolioMutate) {
        // Format phone number if provided
        let formattedPhone = state.profileData.general.phoneNumber;
        if (formattedPhone) {
          // Remove all non-digits and ensure it's 10 digits
          formattedPhone = formattedPhone.replace(/\D/g, '');
        }

        // Update general user info via GraphQL
        const generalInput = {
          type: PortfolioUpdateType.User,
          data: {
            firstName: state.profileData.general.firstName,
            lastName: state.profileData.general.lastName,
            phoneNumber: formattedPhone || undefined,
            userTypes: state.selectedUserTypes,
            email: state.contact.toLowerCase()
          }
        };

        const generalResult = await deps.updatePortfolioMutate({ input: generalInput });

        // Check for GraphQL errors OR mutation failure
        if (generalResult?.errors || !generalResult?.data?.updatePortfolio?.success) {
          const errorMessage =
            generalResult?.errors?.[0]?.message ||
            generalResult?.data?.updatePortfolio?.message ||
            ERROR_MESSAGES.GENERIC;
          logger.error('Failed to save general profile:', errorMessage);
          throw new Error(errorMessage);
        }

        // Update completion status from response
        if (generalResult?.data?.updatePortfolio?.portfolio?.completionStatus) {
          state.portfolioCompletionStatus = toExtensibleProfileData(
            generalResult.data.updatePortfolio.portfolio.completionStatus
          );
        }

        // Save state for recovery
        deps.saveProfileStateToStorage();
      }

      // Save role-specific profile data using Promise.all
      const roleUpdates = state.selectedUserTypes.map(async roleType => {
        const roleKey = roleType.toLowerCase() as keyof typeof state.profileData;
        const roleData = state.profileData[roleKey];

        if (roleData && deps.updatePortfolioMutate) {
          // Each role has its own update type
          const roleInput: UpdatePortfolioInput = {
            type: roleType as PortfolioUpdateType,
            data: roleData as Record<string, unknown>
          };

          const roleResult = await deps.updatePortfolioMutate({ input: roleInput });

          // Check for GraphQL errors OR mutation failure
          if (roleResult?.errors || !roleResult?.data?.updatePortfolio?.success) {
            const errorMessage =
              roleResult?.errors?.[0]?.message ||
              roleResult?.data?.updatePortfolio?.message ||
              ERROR_MESSAGES.PROFILE_INCOMPLETE;
            logger.error(`[FlowControl] Failed to save ${roleType} profile:`, errorMessage);
            // Continue with other roles even if one fails
          } else if (roleResult?.data?.updatePortfolio?.portfolio?.completionStatus) {
            // Update completion status from response
            state.portfolioCompletionStatus = toExtensibleProfileData(
              roleResult.data.updatePortfolio.portfolio.completionStatus
            );
          }
        }
      });

      await Promise.all(roleUpdates);

      // Mark profile as complete in localStorage
      localStorage.setItem('profileComplete', 'true');

      // Update Vuex flag so global guards recognise completion before navigation
      if (deps.rootStore) {
        deps.rootStore.commit(`user/${UserMutationTypes.SET_PROFILE_COMPLETE}`, true);
        await nextTick();
      }

      // Complete and redirect
      if (deps.navigationService) {
        await deps.navigationService.completeFlow();
      } else {
        state.phase = 'complete'; // Fallback
      }

      // Determine redirect target - always ensure we have a fallback
      const redirectTarget = resolvePostAuthRedirect(state.originalRedirect, deps.router);

      // Show success state briefly
      await new Promise(resolve => {
        authResourceManager.setTimeout(() => resolve(undefined), UI_TIMEOUTS.SUCCESS_DISPLAY);
      });

      // Clear intermediate auth state to prevent bootloop on reload
      clearEnhancedAuthState();

      // Navigate via SPA router to avoid full page reload loop
      logger.log('Profile complete, SPA navigating to:', redirectTarget);
      const targetPath =
        typeof redirectTarget === 'string'
          ? redirectTarget
          : redirectTarget.path || '/portfolio/my-programs';

      // Lift the nondismissible lock and clear the auto-open guard
      state.nondismissible = false;
      try {
        sessionStorage.removeItem('auth_dialog_auto_opened');
      } catch (_) {
        /* non-fatal */
      }

      state.isOpen = false;

      // Shared program link path: force full reload to re-trigger ParticipantGuide setup
      // with the authed user (see same comment in completeAndRedirect above).
      if (typeof targetPath === 'string' && targetPath.startsWith('/guide/')) {
        logger.log('Forcing full reload for /guide/ post-profile re-render:', targetPath);
        window.location.assign(targetPath);
        return;
      }

      safeNavigate(deps.router, targetPath, true);
    } catch (error) {
      logger.error('Error completing profile:', error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.GENERIC;
      throw error;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Handle auth completion (called when auth flow completes)
   */
  const handleAuthComplete = async () => {
    logger.log('Handling auth completion');
    const user = deps.getUser?.value;

    if (!user) {
      logger.error('No user found on auth complete');
      if (deps.navigationService) {
        await deps.navigationService.transitionWithError(ERROR_MESSAGES.AUTH_FAILED_NO_USER);
      } else {
        state.phase = 'error'; // Fallback
        state.error = ERROR_MESSAGES.AUTH_FAILED_NO_USER;
      }
      state.isOpen = true; // Ensure dialog shows error
      return;
    }

    try {
      // Check if profile is complete
      const isProfileComplete = deps.rootStore?.getters['user/isProfileComplete'];

      if (!isProfileComplete) {
        // First, load portfolio data to populate existing profile information
        if (deps.loadPortfolioQuery) {
          const portfolioResponse = await deps.loadPortfolioQuery();

          // Populate existing data BEFORE determining phase
          if (portfolioResponse?.data?.userPortfolio) {
            const portfolio = portfolioResponse.data.userPortfolio;

            // Set general profile data from user object
            state.profileData.general = {
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              phoneNumber: user.phoneNumber || '',
              userTypes: user.userTypes || []
            };

            state.selectedUserTypes = user.userTypes || [];

            // Set role-specific data if exists
            if (portfolio.teacher)
              state.profileData.teacher = toExtensibleProfileData(portfolio.teacher) as any;
            if (portfolio.student)
              state.profileData.student = toExtensibleProfileData(portfolio.student) as any;
            if (portfolio.parent)
              state.profileData.parent = toExtensibleProfileData(portfolio.parent) as any;
            if (portfolio.employer)
              state.profileData.employer = toExtensibleProfileData(portfolio.employer) as any;
            if (portfolio.school)
              state.profileData.school = toExtensibleProfileData(portfolio.school) as any;
            if (portfolio.organizer)
              state.profileData.organizer = toExtensibleProfileData(portfolio.organizer) as any;

            // Store completion status for later use
            if (portfolio.completionStatus) {
              state.portfolioCompletionStatus = toExtensibleProfileData(portfolio.completionStatus);
            }
          }

          // Now determine which phase to start at with populated data
          if (deps.determineIncompletePhase) {
            const incompletePhase = deps.determineIncompletePhase(
              user,
              portfolioResponse?.data || null
            );

            // If all profiles complete (incompletePhase === null), route
            // through the standard completion flow — shows the brief
            // "welcome back / complete" success state, syncs the
            // profileComplete flag in localStorage + Vuex, then SPA-navigates
            // to /portfolio/my-programs. Replaces a previous buggy path that
            // tried to "navigate to null" as if it were a phase.
            if (incompletePhase === null) {
              logger.log('All profiles complete — routing through completeAndRedirect');
              await completeAndRedirect();
              return;
            }

            if (deps.navigationService) {
              const navigated = await deps.navigationService.navigate(incompletePhase);
              if (navigated) {
                state.isOpen = true;
              }
            } else {
              state.phase = incompletePhase; // Fallback
              state.isOpen = true;
            }
          } else if (deps.navigationService) {
            // Default to general profile if can't determine
            const navigated = await deps.navigationService.navigate('generalProfile');
            if (navigated) {
              state.isOpen = true;
            }
          } else {
            state.phase = 'generalProfile'; // Fallback
            state.isOpen = true;
          }
        } else if (deps.navigationService) {
          // No portfolio query available, default to general profile
          const navigated = await deps.navigationService.navigate('generalProfile');
          if (navigated) {
            state.isOpen = true;
          }
        } else {
          state.phase = 'generalProfile'; // Fallback
          state.isOpen = true;
        }
      } else {
        // Profile complete, finish auth
        completeAndRedirect();
      }
    } catch (error) {
      logger.error('Error in handleAuthComplete:', error);
      if (deps.navigationService) {
        const navigated = await deps.navigationService.navigate('generalProfile');
        if (navigated) {
          state.isOpen = true;
        }
      } else {
        state.phase = 'generalProfile'; // Fallback to general profile
        state.isOpen = true;
      }
    }
  };

  /**
   * Open the auth dialog
   *
   * @param redirect — optional path to navigate to after auth completes (stored in state.originalRedirect)
   * @param options.nondismissible — if true, dialog cannot be closed by the user until auth completes.
   *        Used when an unauth visitor lands on a shared program link (/guide/{id}/0) — we want to force
   *        them through auth + portfolio setup without letting them dismiss the modal and end up
   *        stuck on a blank participant view.
   */
  const openAuth = async (redirect?: string, options?: { nondismissible?: boolean }) => {
    // Reset resource manager to ensure timeouts work properly
    authResourceManager.reset();

    // Honor the nondismissible flag immediately so the dialog renders without close affordances.
    // Done BEFORE the early-return guards below so the flag is set even on no-op re-entries.
    if (options?.nondismissible) {
      state.nondismissible = true;
    }

    // Prevent concurrent execution
    if (state.loading) {
      logger.log('openAuth already in progress, skipping');
      return;
    }

    // Ensure we're in a clean state before starting
    if (state.phase !== 'idle' && !state.isOpen) {
      logger.log('Resetting stuck phase from:', state.phase);
      if (deps.navigationService) {
        await deps.navigationService.systemTransition('idle');
      } else {
        state.phase = 'idle';
      }
    }

    state.loading = true;
    state.error = null;

    try {
      // Check if redirect is in route query
      const queryRedirect = deps.route?.query?.redirect as string;

      // Fully decode redirect param to avoid double-encoding issues
      const decodeLoop = (val: string): string => {
        let prev = val;
        let cur = val;
        try {
          let hasMoreDecoding = true;
          while (hasMoreDecoding) {
            const decoded = decodeURIComponent(prev);
            if (decoded === prev) {
              hasMoreDecoding = false;
            } else {
              prev = decoded;
              cur = decoded;
            }
          }
        } catch (e) {
          cur = prev; // not URI encoded – keep original
        }
        return cur;
      };

      let redirectTarget = redirect || queryRedirect || null;
      if (redirectTarget) {
        redirectTarget = decodeLoop(redirectTarget);
        // Ensure leading slash
        if (!redirectTarget.startsWith('/')) {
          redirectTarget = `/${redirectTarget.replace(/^\/+/, '')}`;
        }
      }

      logger.log('Opening auth, redirect:', redirectTarget);
      state.originalRedirect = redirectTarget;

      // Check if user is already logged in
      const user = deps.getUser?.value;
      if (user) {
        // Try to recover from enhanced auth state
        const enhancedState = loadEnhancedAuthState();
        if (
          enhancedState &&
          validateAuthState(enhancedState) &&
          enhancedState.phase !== 'idle' &&
          enhancedState.phase !== 'complete'
        ) {
          // Recover the full session including navigation history
          logger.log('Recovering complete auth state at phase:', enhancedState.phase);

          // Restore all state fields including complete profile data
          Object.assign(state, {
            phase: enhancedState.phase,
            previousPhase: enhancedState.previousPhase || 'idle',
            phaseHistory: enhancedState.phaseHistory || [],
            completedPhases: enhancedState.completedPhases || new Set(),
            maxReachedPhase: enhancedState.maxReachedPhase || 'idle',
            encounteredPhases: enhancedState.encounteredPhases || new Set(),
            lockedPhases: enhancedState.lockedPhases || new Set(),
            criticalDecisions: enhancedState.criticalDecisions || new Map(),
            conditionalTriggers: enhancedState.conditionalTriggers || new Map(),
            profileData: {
              general: enhancedState.profileData?.general || null,
              teacher: enhancedState.profileData?.teacher || null,
              student: enhancedState.profileData?.student || null,
              parent: enhancedState.profileData?.parent || null,
              employer: enhancedState.profileData?.employer || null,
              school: enhancedState.profileData?.school || null,
              organizer: enhancedState.profileData?.organizer || null
            },
            selectedUserTypes: enhancedState.selectedUserTypes || [],
            currentRoleIndex: enhancedState.currentRoleIndex || 0,
            contact: enhancedState.contact || '',
            isEmail: enhancedState.isEmail !== undefined ? enhancedState.isEmail : true,
            pendingIdentityLink: enhancedState.pendingIdentityLink || {
              type: null,
              value: '',
              otpSent: false
            },
            passedGates: enhancedState.passedGates || new Set(),
            resolvedConflicts: enhancedState.resolvedConflicts || new Set(),
            alternateContactSkipped: enhancedState.alternateContactSkipped || false,
            discoveredAccountId: enhancedState.discoveredAccountId || null,
            portfolioCompletionStatus: enhancedState.portfolioCompletionStatus || null,
            // Restore persisted redirect if no fresh redirect was provided
            originalRedirect: redirectTarget || enhancedState.originalRedirect || null,
            recoveryAvailable: false
          });

          profileStorage.clearPromptShown();

          // Only clear profileComplete when recovering to a genuine profile-setup
          // phase. For stale non-profile phases (otp, password, contact, etc.)
          // clearing it would cause a false "incomplete profile" state and bootloop.
          const PROFILE_SETUP_PHASES: AuthPhase[] = [
            'generalProfile',
            'teacherProfile',
            'studentProfile',
            'parentProfile',
            'employerProfile',
            'schoolProfile',
            'organizerProfile'
          ];
          if (PROFILE_SETUP_PHASES.includes(enhancedState.phase as AuthPhase)) {
            localStorage.removeItem('profileComplete');
            if (deps.rootStore) {
              deps.rootStore.commit(`user/${UserMutationTypes.SET_PROFILE_COMPLETE}`, false);
            }
          } else {
            // Stale non-profile phase — clear it and fall through to handleAuthComplete
            logger.log('Stale non-profile recovery phase:', enhancedState.phase, '— clearing');
            clearEnhancedAuthState();
            await handleAuthComplete();
            return; // skip the rest of the recovery block
          }

          // Show dialog after recovery
          state.isOpen = true;
        } else {
          // No recovery available, check profile completion
          await handleAuthComplete();
        }
      } else if (deps.navigationService) {
        // Not logged in, start at contact phase
        const navigated = await deps.navigationService.navigate('contact');
        if (navigated) {
          logger.log('Navigation successful, setting isOpen to true');
          state.isOpen = true;
          logger.log('state.isOpen is now:', state.isOpen);
        } else if (state.phase === 'contact') {
          // Already at contact phase, just open the dialog
          logger.log('Already at contact phase, opening dialog');
          state.isOpen = true;
        }
      } else {
        state.phase = 'contact'; // Fallback
        state.isOpen = true;
      }
    } catch (error) {
      logger.error('Error opening auth:', error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.GENERIC;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Reset the entire auth flow
   */
  const resetAuthFlow = () => {
    logger.log('Resetting auth flow');
    closeAuth();
    clearEnhancedAuthState();
    profileStorage.clearSession();
    localStorage.removeItem('profileComplete');
  };

  /**
   * Externally release the nondismissible lock (e.g. when a silent token refresh
   * authenticates the user while the persistent dialog is still open).
   */
  const releaseNondismissible = () => {
    state.nondismissible = false;
  };

  /**
   * Switch the auth dialog into (or out of) express-enroll mode. Called by
   * ParticipantGuide once it knows the Program opts into expressEnroll. Flipping
   * this while the dialog is on the 'contact' phase reactively swaps ContactPhase
   * into its name+contact express form — no re-open, no race with App.vue.
   */
  const setAuthMode = (mode: 'standard' | 'express') => {
    state.authMode = mode;
  };

  /**
   * Set (or clear) the active program access policy. Called by the
   * public-program flow once it knows the Program's accessPolicy. Drives the
   * adaptive ladder rendered by ContactPhase / UnifiedAuthDialog. Pass null to
   * fall back to the legacy express/standard behavior.
   *
   * @param policy     normalized AccessPolicy (or null to clear)
   * @param programId  program being enrolled into — needed by the anonymous
   *                   "Join now" (floor:'off') action.
   */
  const setAccessPolicy = (policy: AccessPolicy | null, programId = '') => {
    // Authoritative kill-switch: when ACCESS_POLICY is off, the ladder must
    // never light up regardless of caller. Force-clear so even a future caller
    // that sets a policy cannot activate the adaptive ladder UI.
    if (!FEATURE_FLAGS.ACCESS_POLICY) {
      state.accessPolicy = null;
      state.accessPolicyProgramId = '';
      return;
    }
    state.accessPolicy = policy;
    state.accessPolicyProgramId = programId;
  };

  /**
   * Tell the sign-in box which program the visitor is joining so ContactPhase
   * can show the name, organizer, price, time and credential above the email
   * field. Pass null to go back to the plain "Enter PilotCity" box.
   */
  const setProgramContext = (ctx: ProgramJoinContext | null) => {
    state.programContext = ctx;
  };

  /**
   * A contact the visitor already typed on the landing (L15 arm D): the contact phase
   * submits it as soon as it opens. Cleared by the phase once used.
   */
  const setContactPrefill = (prefill: { value: string; isEmail: boolean } | null) => {
    state.contactPrefill = prefill;
  };

  return {
    setProgramContext,
    setContactPrefill,
    openAuth,
    closeAuth,
    completeAndRedirect,
    completeProfileAndRedirect,
    handleAuthComplete,
    resetAuthFlow,
    releaseNondismissible,
    setAuthMode,
    setAccessPolicy
  };
}
